import { useEffect } from 'react'

/**
 * Publishes the browser's REAL viewport height as `--app-vh` on <html>.
 *
 * Some Android tablet browsers resolve CSS viewport units (100dvh/100vh)
 * against a stale or plain wrong layout viewport — notoriously after an
 * orientation change — which made the admin shell taller than the screen:
 * the floor map, action rail and sidebar all ran below the fold with no way
 * to scroll (every layer is overflow-hidden by design). `window.innerHeight`
 * / `visualViewport.height` are always the truth, so the shell sizes from
 * this variable instead, with `100dvh` as the fallback where the effect
 * hasn't run yet:  height: calc(var(--app-vh, 100dvh) - …).
 *
 * Renders nothing.
 */
export function AppViewportVar() {
  useEffect(() => {
    const root = document.documentElement
    let raf = 0
    const update = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        // innerHeight, not visualViewport.height: the visual viewport shrinks
        // under the on-screen keyboard, which would squash the whole shell
        // while typing. innerHeight tracks the layout viewport — the thing
        // 100dvh is SUPPOSED to equal.
        const h = Math.round(window.innerHeight || window.visualViewport?.height || 0)
        if (h > 0) root.style.setProperty('--app-vh', `${h}px`)
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    window.visualViewport?.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
      window.visualViewport?.removeEventListener('resize', update)
      root.style.removeProperty('--app-vh')
    }
  }, [])
  return null
}
